import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BarChart3, Focus, GraduationCap, Menu, Sparkles, Workflow } from 'lucide-react';
import { DropdownMenuItem, DropdownMenuLabel } from '@dashai/components/ui/dropdown-menu';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from '@dashai/components/ui/navigation-menu';
import { useExportStyle } from '@dashai/hooks/useExportStyle';
import { useIsNarrowViewport } from '@dashai/hooks/useIsNarrowViewport';
import { usePersonaVisibility } from '@dashai/hooks/usePersonaVisibility';
import { DASHAI_ROUTES } from '@dashai/lib/browseNavigation';
import type { LaunchWorkflow } from '@dashai/lib/browseNavigation';
import type { ChatSession } from '@dashai/types/dashai';
import { UserProfileDropdown } from '../UserProfileDropdown';
import { PERSONA_DEFINITIONS, PREMISE_LABELS } from '@/data/personaDefinitions';
import { DASHBOARD_CARDS } from '@/data/dashboardCards';
import { useWorkflowsLabel } from '@/hooks/useWorkspaceUiConfig';
import {
  DashAIPanel,
  DashboardsPanel,
  PanelLink,
  PanelSeparator,
  SplitTopMenu,
  TopMenu,
  WorkflowsPanel,
  inPageClick,
} from './TopBarMenus';
import type { DashAIActions } from './TopBarMenus';
import { navPillClass, TOP_BAR_SIDE_INSET } from './navStyles';

// The panels whose opening shows the DashAI entries, which fetch the recent
// chats: the DashAI panel, and on a narrow screen the one combined menu.
const DASHAI_PANEL = 'dashai';
const MENU_PANEL = 'menu';

// The navy bar, after DashLeads' top navigation. The bottom rule is set
// inline: the `.dashai-scope` border reset would otherwise repaint it.
const BAR_CLASS = 'flex h-20 shrink-0 items-center gap-3 border-b bg-topbar';
const BAR_STYLE = {
  borderBottomColor: 'hsl(var(--topbar-foreground) / 0.1)',
  paddingInline: TOP_BAR_SIDE_INSET,
};

export interface TopBarProps {
  // Supplied inside DashAI; absent on analytics pages. See LaunchWorkflow.
  onLaunchWorkflow?: LaunchWorkflow;
  // Supplied inside DashAI, so its own destinations change the view in place.
  dashAIActions?: DashAIActions;
  // The chats the DashAI panel lists its two latest from.
  recentChats?: ChatSession[];
  // True while a host that fetches on open is loading its first list.
  recentChatsLoading?: boolean;
  // Called as the DashAI panel opens, so a host without a loaded chat list
  // fetches the recent chats only when they are about to show.
  onOpenDashAIPanel?: () => void;
  // Account menu: the same props the sidebar's profile footer takes.
  userName: string;
  userEmail: string;
  userPhotoUrl?: string;
  allowedClients: string[];
  workspaceListError?: boolean;
  workspaceListLoaded?: boolean;
  onRetryWorkspaceList?: () => void;
  currentClient: string | null;
  onClientChange: (client: string) => void;
  onSignOut: () => void;
  onAccountProfile?: () => void;
  onProfileMemory?: () => void;
  onAdminPanel?: () => void;
  isAdmin?: boolean;
  isStaff?: boolean;
  canAccessUserManagement?: boolean;
  pinnedWorkspaces?: string[];
  focusMode?: boolean;
  onTogglePinWorkspace?: (client: string) => void;
  onToggleFocusMode?: () => void;
}

export function TopBar(props: TopBarProps) {
  const { onLaunchWorkflow, dashAIActions, recentChats, recentChatsLoading, onOpenDashAIPanel, onAccountProfile, currentClient } = props;
  const location = useLocation();
  const isNarrow = useIsNarrowViewport();
  const visibility = usePersonaVisibility(currentClient ?? null);
  const workflowsLabel = useWorkflowsLabel();
  // The account button shows the workspace's company logo, taken from its
  // export style, the branding its decks carry.
  const { style: exportStyle } = useExportStyle(currentClient ?? undefined);
  const workspaceLogoUrl = exportStyle?.logoHeaderUrl || exportStyle?.logoMainUrl;
  // The open panel is controlled so an in-page launch can close it. Every
  // other entry is a link, and following a link closes its panel.
  const [openPanel, setOpenPanelState] = useState('');
  const setOpenPanel = (value: string) => {
    if (value === DASHAI_PANEL || value === MENU_PANEL) onOpenDashAIPanel?.();
    setOpenPanelState(value);
  };
  const closePanel = () => setOpenPanelState('');
  const launchWorkflow: LaunchWorkflow | undefined = onLaunchWorkflow
    ? (agentId, variantPrefill) => {
        closePanel();
        onLaunchWorkflow(agentId, variantPrefill);
      }
    : undefined;
  const dashAIPanel = (
    <DashAIPanel
      recentChats={recentChats}
      recentChatsLoading={recentChatsLoading}
      actions={dashAIActions}
      closePanel={closePanel}
    />
  );

  const onDashboard = DASHBOARD_CARDS.some((card) => location.pathname.startsWith(card.route));
  const onDashAI = location.pathname === '/ai' || location.pathname.startsWith('/ai/');
  const onLearn = location.pathname.startsWith('/learn');

  const persona = visibility.config ? PERSONA_DEFINITIONS[visibility.config.persona] : null;
  const premise = visibility.config?.premise ? PREMISE_LABELS[visibility.config.premise] : null;
  // The role line opens the role setup when the host supplies it, and is a
  // plain label otherwise, so it never offers an action that does nothing.
  let roleHeader: React.ReactNode = null;
  if (persona && onAccountProfile) {
    roleHeader = (
      <DropdownMenuItem onSelect={onAccountProfile} className="mx-1 mb-1 cursor-pointer flex-col items-start gap-0 rounded-md bg-muted/60">
        <span className="text-sm font-medium">{persona.label}</span>
        <span className="text-xs text-muted-foreground">{premise ? `${premise} · ` : ''}Edit role</span>
      </DropdownMenuItem>
    );
  } else if (persona) {
    roleHeader = (
      <DropdownMenuLabel className="mx-1 mb-1 flex flex-col rounded-md bg-muted/60 font-normal">
        <span className="text-sm font-medium">{persona.label}</span>
        {premise && <span className="text-xs text-muted-foreground">{premise}</span>}
      </DropdownMenuLabel>
    );
  }

  const learnLink = (
    <NavigationMenuItem>
      <NavigationMenuLink asChild active={onLearn}>
        <Link to="/learn" className={navPillClass(onLearn)}>
          <GraduationCap className="h-4 w-4" aria-hidden="true" />
          Learn
        </Link>
      </NavigationMenuLink>
    </NavigationMenuItem>
  );

  // Below the narrow breakpoint every section folds into one panel, in the
  // order the wide bar shows them.
  const navigation = (
    <NavigationMenu aria-label="Main" value={openPanel} onValueChange={setOpenPanel}>
      <NavigationMenuList className="gap-1 xl:gap-3">
        {isNarrow ? (
          <TopMenu value={MENU_PANEL} label="Menu" icon={Menu} contentClassName="w-72 max-h-[80vh] overflow-y-auto">
            <p className="px-2 pt-1.5 text-sm font-semibold">Dashboards</p>
            <DashboardsPanel visibility={visibility} />
            <p className="px-2 pt-3 text-sm font-semibold">{workflowsLabel.title}</p>
            <WorkflowsPanel visibility={visibility} onLaunchWorkflow={launchWorkflow} label={workflowsLabel} />
            <p className="px-2 pt-3 text-sm font-semibold">DashAI</p>
            {dashAIPanel}
            <PanelSeparator />
            <PanelLink to="/learn" icon={GraduationCap}>Learn</PanelLink>
          </TopMenu>
        ) : (
          <>
            <TopMenu value="dashboards" label="Dashboards" icon={BarChart3} active={onDashboard} contentClassName="w-60">
              <DashboardsPanel visibility={visibility} />
            </TopMenu>
            <TopMenu value="workflows" label={workflowsLabel.title} icon={Workflow} contentClassName="w-72 max-h-[70vh] overflow-y-auto">
              <WorkflowsPanel visibility={visibility} onLaunchWorkflow={launchWorkflow} label={workflowsLabel} />
            </TopMenu>
            <SplitTopMenu
              value={DASHAI_PANEL}
              label="DashAI"
              icon={Sparkles}
              to={DASHAI_ROUTES.chatPage}
              active={onDashAI}
              onClick={inPageClick(dashAIActions?.onOpenChatPage, closePanel)}
              contentClassName="w-64"
            >
              {dashAIPanel}
            </SplitTopMenu>
            {learnLink}
          </>
        )}
      </NavigationMenuList>
    </NavigationMenu>
  );

  return (
    <header className={BAR_CLASS} style={BAR_STYLE} data-testid="top-bar">
      {isNarrow && navigation}

      <Link
        to="/"
        onClick={inPageClick(dashAIActions?.onHome, closePanel)}
        className="mr-8 flex items-center gap-3 rounded-md py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-topbar-foreground/40"
        aria-label="DashONE home"
      >
        {/* The product name as text, one word with the first half bold, in the
            system font stack DashLeads renders its own wordmark in. */}
        <span className="font-[ui-sans-serif,system-ui,sans-serif] text-2xl font-medium text-topbar-foreground">
          <span className="font-bold">Dash</span>ONE
        </span>
      </Link>

      {!isNarrow && navigation}

      <div className="flex-1" />
      {props.isStaff && props.focusMode && (
        <span role="status" className="flex shrink-0 items-center gap-1.5 rounded-full bg-topbar-foreground/10 px-2 py-1 text-xs text-topbar-foreground">
          <Focus aria-hidden="true" className="h-3.5 w-3.5" />Presenting on
        </span>
      )}
      <div className="h-6 w-px bg-topbar-foreground/10" aria-hidden="true" />

      <UserProfileDropdown
        trigger="topbar"
        header={roleHeader}
        userName={props.userName}
        userEmail={props.userEmail}
        userPhotoUrl={props.userPhotoUrl}
        workspaceLogoUrl={workspaceLogoUrl}
        allowedClients={props.allowedClients}
        workspaceListError={props.workspaceListError}
        workspaceListLoaded={props.workspaceListLoaded}
        onRetryWorkspaceList={props.onRetryWorkspaceList}
        currentClient={currentClient}
        onClientChange={props.onClientChange}
        onSignOut={props.onSignOut}
        onAccountProfile={onAccountProfile}
        onProfileMemory={props.onProfileMemory}
        onAdminPanel={props.onAdminPanel}
        isAdmin={props.isAdmin}
        isStaff={props.isStaff}
        canAccessUserManagement={props.canAccessUserManagement}
        pinnedWorkspaces={props.pinnedWorkspaces}
        focusMode={props.focusMode}
        onTogglePinWorkspace={props.onTogglePinWorkspace}
        onToggleFocusMode={props.onToggleFocusMode}
      />
    </header>
  );
}
